import React from "react";
import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { LinkBuildingDashboard } from "@/components/LinkBuildingDashboard";
import { BacklinkEmailTemplates } from "@/components/agent/BacklinkEmailTemplates";
import { KeywordImporter } from "@/components/agent/KeywordImporter";
import { SEOMonitoringDashboard } from "@/components/SEOMonitoringDashboard";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link2, BarChart3, Target, FileText, Mail, Database } from "lucide-react";
export default function SEODashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/agent-login");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Link Building & SEO Dashboard"
        description="Comprehensive link building and SEO keyword tracking dashboard for Aureum VII Trading's digital marketing strategy."
        type="website"
        url="/seo-dashboard"
        noindex
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-8 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <Badge variant="outline" className="border-accent/30 text-accent bg-accent/10 mb-4">
              SEO Management
            </Badge>
            <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-light mb-4 tracking-tight text-primary">
              Link Building Dashboard
            </h1>
            <p className="font-inter text-lg text-muted-foreground max-w-2xl mx-auto">
              Track keywords, monitor backlink acquisition, and manage outreach campaigns for B2B growth
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-8">
        <BreadcrumbNav items={[
          { label: "Home", href: "/" },
          { label: "Agent Dashboard", href: "/agent-dashboard" },
          { label: "SEO Dashboard" }
        ]} />

        {/* Quick Stats Banner */}
        <div className="mt-6 mb-8 p-4 bg-primary/5 rounded-xl">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <span className="font-medium">Keyword Research</span>
            </div>
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-blue-600" />
              <span className="font-medium">Backlink Tracking</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              <span className="font-medium">Content Opportunities</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-accent" />
              <span className="font-medium">Progress Monitoring</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              <span className="font-medium">Email Templates</span>
            </div>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-indigo-600" />
              <span className="font-medium">Keyword Database</span>
            </div>
          </div>
        </div>

        {/* SEO Monitoring Dashboard */}
        <section className="mb-8">
          <SEOMonitoringDashboard />
        </section>

        {/* Keyword Importer */}
        <section className="mb-8">
          <KeywordImporter />
        </section>

        {/* Main Dashboard */}
        <LinkBuildingDashboard />

        {/* Email Templates Section */}
        <section className="mt-12">
          <BacklinkEmailTemplates />
        </section>

        {/* Quick Actions */}
        <section className="mt-12 py-8 bg-muted/30 rounded-2xl px-8">
          <h2 className="text-xl font-bold text-center mb-6">Quick Actions</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="outline" onClick={() => navigate("/agent-dashboard")}>
              Back to Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate("/blog")}>
              View Blog Content
            </Button>
            <Button variant="outline" onClick={() => navigate("/smart-toilets")}>
              Product Catalog
            </Button>
            <Button onClick={() => navigate("/compare")}>
              Product Comparison Tool
            </Button>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
